import type { ClipProcessingEvent, ProcessingStage, StageStatus } from "@/hooks/useClips"

// Match the backend's attempt ordering: terminal > running > pending, then the
// last event (the endpoint returns insertion order). Late callbacks cannot reopen
// a finished attempt or override a newer retry.
export function latestStageEvent(stage: ProcessingStage, events: ClipProcessingEvent[]) {
  const rank = (status: StageStatus) => status === "pending" ? 0 : status === "running" ? 1 : 2
  return events.reduce<ClipProcessingEvent | undefined>((latest, event) => {
    if (event.stage !== stage) return latest
    if (!latest || event.attempt > latest.attempt ||
      (event.attempt === latest.attempt && rank(event.status) >= rank(latest.status))) {
      return event
    }
    return latest
  }, undefined)
}

// The real-play window. A clip longer than this is game film / a leftover tail, NOT a
// play, so the AI stage will never run for it (and for a VIRTUAL clip, neither will the
// per-play cover). Mirrors the backend's LONG_CLIP_MAX_SECONDS
// (backend/src/constants/clips.ts, epic #685): ≤40s → real play (AI runs), >40s → no AI.
// Hardcoded here because the admin app has no backend import; if the backend threshold
// changes (#733) this must change with it.
export const REAL_PLAY_MAX_SECONDS = 40

// The subset of a Clip this helper needs. Loose by design so it's trivial to unit test
// without constructing a whole Clip row.
export interface StageStatusClip {
  start_seconds: number | null
  end_seconds: number | null
  video_meta: unknown
  transcode_status: StageStatus
  cover_status: StageStatus
  ai_status: StageStatus
}

// Clip duration in seconds, or null when this is a whole-file clip (start_seconds null)
// or the bounds are missing. A clip is "virtual" iff start_seconds != null.
export function clipDurationSeconds(clip: {
  start_seconds: number | null
  end_seconds: number | null
}): number | null {
  if (clip.start_seconds == null || clip.end_seconds == null) return null
  return clip.end_seconds - clip.start_seconds
}

// A whole-file clip's duration as stamped on video_meta by the transcode callback.
// Mirrors the backend's pickClipDurationSeconds precedence (backend/src/lib/clip-extras.ts):
// transcoded.duration → original.duration → legacy top-level duration. Null when no
// duration has been reported yet (e.g. the clip hasn't finished transcoding).
export function videoMetaDurationSeconds(meta: unknown): number | null {
  const m = meta as
    | { transcoded?: { duration?: unknown }; original?: { duration?: unknown }; duration?: unknown }
    | null
    | undefined
  const duration = [m?.transcoded?.duration, m?.original?.duration, m?.duration].find(
    (d): d is number => typeof d === "number"
  )
  return duration ?? null
}

// Derive the DISPLAY status for one stage so non-processed stages don't masquerade as
// "pending" when they will NEVER run. The backend already display-derives legacy
// `succeeded` from artifacts; this is the complementary client-side derivation of
// `skipped` for stages that are out of scope for the clip.
//
// Rules:
//   - A worker-set status (running/succeeded/failed) is authoritative — return it as-is.
//   - Stored `pending` on a stage that can never run → `skipped`:
//       * ai: a clip whose duration is KNOWN and OUTSIDE the real-play window
//         (> REAL_PLAY_MAX_SECONDS). This mirrors the pipeline's 40s AI gate, which
//         applies to whole-file clips too — a ≤40s whole-file upload IS a cropped play
//         and gets AI on transcode completion, and an unknown duration also runs AI
//         (legacy behavior), so neither shows "skipped". Virtual duration = end - start;
//         whole-file duration comes from video_meta (videoMetaDurationSeconds).
//       * cover: a VIRTUAL clip outside the real-play window — game film / leftover
//         tail, no per-play cover. (A whole-file clip still gets the game cover.)
//   - Otherwise (a clip still awaiting work, or duration unknown) → keep `pending`.
//   - transcode is never skipped here (every clip is transcoded).
export function effectiveStageStatus(
  stage: ProcessingStage,
  clip: StageStatusClip
): StageStatus {
  const stored =
    stage === "transcode"
      ? clip.transcode_status
      : stage === "cover"
        ? clip.cover_status
        : clip.ai_status

  // A status a worker actually set always wins over derivation.
  if (stored !== "pending") return stored
  if (stage === "transcode") return stored

  const isVirtual = clip.start_seconds != null
  const duration = isVirtual ? clipDurationSeconds(clip) : videoMetaDurationSeconds(clip.video_meta)

  // ai outside the real-play window won't run — whole-file or virtual — but only when
  // the duration is KNOWN; with an unknown duration the pipeline still runs AI, so the
  // stage stays pending.
  if (stage === "ai" && duration != null && duration > REAL_PLAY_MAX_SECONDS) {
    return "skipped"
  }

  // cover on a virtual clip outside the real-play window won't run.
  if (stage === "cover" && isVirtual && duration != null && duration > REAL_PLAY_MAX_SECONDS) {
    return "skipped"
  }

  return stored
}
